import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { FolderLock, Loader2, Download, ShieldAlert, FileText, Lock, Upload } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const SCAN = {
  clean: { label: "Clean", cls: "bg-emerald-100 text-emerald-700" },
  quarantined: { label: "Quarantined", cls: "bg-red-100 text-red-700" },
  pending: { label: "Pending scan", cls: "bg-amber-100 text-amber-800" },
  infected: { label: "Infected", cls: "bg-red-100 text-red-700" },
  failed: { label: "Scan failed", cls: "bg-red-100 text-red-700" },
};
const INACCESSIBLE = new Set(["quarantined", "pending", "infected", "failed"]);

export default function VaultPanel({ deal }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({ name: "", doc_type: "purchase_agreement", url: "", sha256: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/deals/${deal.deal_id}/vault`);
      setDocs(data.documents || []);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [deal.deal_id]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!form.name.trim()) { toast.error("Document name is required"); return; }
    setBusy("up");
    try {
      await api.post(`/deals/${deal.deal_id}/vault`, { ...form, url: form.url || "https://vault.example/doc" });
      setForm({ name: "", doc_type: "purchase_agreement", url: "", sha256: "" });
      toast.success("Document added to the vault");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const download = async (doc) => {
    setBusy(doc.doc_id);
    try {
      const { data } = await api.get(`/vault/${doc.doc_id}/download`);
      toast.success("Access granted (audited)");
      if (data.url) window.open(data.url, "_blank", "noopener");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  // Group by category (doc_type).
  const groups = docs.reduce((a, d) => { (a[d.doc_type] = a[d.doc_type] || []).push(d); return a; }, {});

  return (
    <div className="mt-6 border-t border-slate-200 pt-5" data-testid="vault-panel">
      <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700"><FolderLock size={15} /> Document vault</h4>

      <div className="mb-4 grid grid-cols-1 gap-2 rounded-lg border border-slate-200 p-3 sm:grid-cols-4" data-testid="vault-upload">
        <input data-testid="vault-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Document name" className="h-9 rounded-md border border-slate-300 px-3 text-sm sm:col-span-2" />
        <select data-testid="vault-type" value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })}
          className="h-9 rounded-md border border-slate-300 px-2 text-sm">
          {["purchase_agreement", "invoice", "inspection_report", "bill_of_lading", "insurance_cert", "customs_doc", "proof_of_delivery", "other"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button data-testid="vault-upload-btn" onClick={upload} disabled={busy === "up"}
          className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md bg-[#2563EB] px-3 text-sm font-medium text-white disabled:opacity-60">
          <Upload size={13} /> Add
        </button>
      </div>

      {loading ? <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div> : (
        <div className="space-y-4" data-testid="vault-list">
          {docs.length === 0 && <p className="text-sm text-slate-400">No documents yet.</p>}
          {Object.entries(groups).map(([cat, items]) => (
            <div key={cat} data-testid={`vault-cat-${cat}`}>
              <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{cat.replace(/_/g, " ")}</div>
              <div className="space-y-1.5">
                {items.map((d) => {
                  const scan = SCAN[d.scan_status] || SCAN.pending;
                  const locked = INACCESSIBLE.has(d.scan_status);
                  return (
                    <div key={d.doc_id} data-testid={`vault-doc-${d.doc_id}`}
                      className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <FileText size={14} className="shrink-0 text-slate-400" />
                          <span className="truncate font-medium text-[#0A0F1C]">{d.name}</span>
                          <span className="rounded bg-slate-100 px-1.5 text-[10px] text-slate-500">v{d.version}{d.current ? " · current" : ""}</span>
                          {d.legal_hold && <span className="rounded bg-red-100 px-1.5 text-[10px] font-semibold text-red-700">LEGAL HOLD</span>}
                        </div>
                        <div className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-slate-400">
                          <span className={`rounded px-1.5 ${scan.cls}`}>{scan.label}</span>
                          <span>Uploader: {String(d.uploaded_by).slice(0, 10)}</span>
                          {d.retention_expires_at && <span>Expires: {d.retention_expires_at}</span>}
                        </div>
                      </div>
                      {locked ? (
                        <span data-testid={`vault-locked-${d.doc_id}`} className="inline-flex shrink-0 items-center gap-1 rounded border border-red-200 px-2 py-1 text-[11px] text-red-600">
                          <ShieldAlert size={11} /> Inaccessible
                        </span>
                      ) : (
                        <button data-testid={`vault-download-${d.doc_id}`} onClick={() => download(d)} disabled={busy === d.doc_id}
                          className="inline-flex shrink-0 items-center gap-1 rounded border border-slate-300 px-2 py-1 text-[11px] text-slate-600 hover:border-slate-400">
                          <Download size={11} /> View
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="mt-3 flex items-center gap-1 text-[11px] text-slate-400"><Lock size={11} /> Reviewed/signed files are versioned (never silently overwritten); every view & download is audited.</p>
    </div>
  );
}
